import React, { useState } from 'react';
import moment from 'moment';
import i18next from '../../i18n.js';
import { transportationMethods, tripDestinations, routeCreatingTypes } from '../../constants.js';
import { findField } from '../documents/DocumentHelpers.js';
import { getWordWithCorrectEnding } from '../helpers/HelperFunctions.js';
import InfoBox from './InfoBox.js';
import TripDocuments from './TripDocuments.js';
import { Icon } from './TripIcons.js';

const transportIcon = (method) => {
    switch (method) {
        case transportationMethods.plane: return 'plane';
        case transportationMethods.bus: return 'bus';
        case transportationMethods.train: return 'train';
        case transportationMethods.autoColleagues:
        case transportationMethods.ownCar:
        case transportationMethods.corporateCar:
        case transportationMethods.carRental: return 'car';
        default: return 'train';
    }
}

// Рядок таймлайна: іконка + основний текст (або пара «звідки/куди») + стрілка праворуч
const Row = (props) => (
    <div className={'trip-summary-row' + (props.green ? ' green' : '')}>
        <Icon name={props.icon} />
        {props.places
            ? <div className="row-places">
                <span>{props.places[0]}</span>
                <div className="places-divider" />
                <span>{props.places[1]}</span>
            </div>
            : <div className="row-content">
                <div className="main">{props.main}</div>
                {props.sub && <div className="sub">{props.sub}</div>}
            </div>}
        {props.trailing && <div className="trailing"><Icon name={props.trailing} size={20} /></div>}
    </div>
);

// Підсумок відрядження за дизайном: таймлайн маршрутів, додані документи, інформаційний блок
// і коментарі. Нічого не редагує — тільки показує вже введені властивості документа.
const TripSummary = (props) => {
    const { properties, fields } = props;
    const [commentsOpened, setCommentsOpened] = useState(false);


    const routeFormField = findField(fields, 'routeForm');
    const dictLabel = (fieldName, value) =>
        findField(routeFormField?.fields ?? [], fieldName)?.values?.data?.find(item => item.value === value)?.label;

    const fmt = (value) => value ? moment(value.toString().split('T')[0]).format('DD.MM.YYYY') : '';
    const range = (from, to) => [fmt(from), fmt(to)].filter(Boolean).join(' – ');
    const daysCount = (from, to) => from && to
        ? (moment(to.toString().split('T')[0]).diff(moment(from.toString().split('T')[0]), 'days') + 1)
        : null;
    const daysText = (from, to, additionalDays = 0) => {
        const days = daysCount(from, to);
        return days ? `${days} ${getWordWithCorrectEnding(days, 'день')}` : '';
    }

    const nightsText = (from, to) => {
        let nights = daysCount(from, to) - 1;
        if (nights == 0) nights = 1;
        return nights ? `${nights} ${getWordWithCorrectEnding(nights, 'ніч')}` : '';
    }

    const abroad = properties['sx:btBusinessTripDestination'] === tripDestinations.abroad;
    const multi = !!properties['sx:btMultipleRoutes'];
    // маршрут формує Travel-менеджер, тому транспорту й проживання в підсумку ще немає
    const autoMode = abroad && properties['sx:btRouteType'] === routeCreatingTypes.auto;

    const routes = properties['routeForm'] ?? [];
    const backForm = (properties['goingBackForm'] ?? [])[0];
    const departureCity = properties['sx:btMainPlace'] ? properties['sx:btCityReturn'] : properties['sx:btReasonWorkPlace'];

    // дати й точки маршруту живуть у вкладеній формі travelForm, проживання — в residenceStatusForm
    const travel = (form) => (form?.properties?.['travelForm'] ?? [])[0]?.properties ?? {};
    const residence = (form) => (form?.properties?.['residenceStatusForm'] ?? [])[0]?.properties;

    const routeCity = (form) => form?.properties?.['sx:pbtCity'] || form?.properties?.['sx:pbtLocality'] || '';
    const routeTitle = (form, index) =>
        dictLabel('sx:pbtTripGoal', form.properties['sx:pbtTripGoal']) || dictLabel('sx:pbtTripGoalForeign', form.properties['sx:pbtTripGoalForeign']) ||
        `${i18next.t('document.tripSummaryRoute')} ${index + 1}`;
    // поки маршрут не заповнений, точки підставляються з міста відправлення й міста призначення
    const routeFrom = (form, index) => travel(form)['sx:tfPoint'] || (index === 0 ? departureCity : routeCity(routes[index - 1])) || '';
    const routeTo = (form) => travel(form)['sx:tfPointArrival'] || routeCity(form) || '';

    // повернення: у кількох маршрутах це окрема форма, в одному — поля зворотного шляху маршруту
    const getBackInfo = () => {
        if (multi) {
            if (!backForm) return null;
            return {
                method: backForm.properties['sx:pbtMethodTransportation'],
                date: travel(backForm)['sx:tfArrivalDate'],
                from: routeCity(routes[routes.length - 1]),
                to: departureCity
            };
        }
        const firstRoute = routes[0];
        if (!firstRoute) return null;
        const sameTransportBack = firstRoute.properties['sx:pbtSameTransportBack'];
        return {
            method: sameTransportBack ? firstRoute.properties['sx:pbtMethodTransportation'] : firstRoute.properties['sx:pbtBackMethodTransportation'],
            date: sameTransportBack ? travel(firstRoute)['sx:tfArrivalDate'] : firstRoute.properties['dateStartBack'],
            from: routeTo(firstRoute),
            to: departureCity
        };
    }
    const backInfo = getBackInfo();

    // сегмент триває від свого виїзду до виїзду наступного маршруту
    const segmentEndDate = (index) => {
        const nextRoute = routes[index + 1];
        if (nextRoute) return travel(nextRoute)['sx:tfDepartureDate'];
        return backInfo?.date || properties['sx:btDateEnd'];
    }

    const needTaxi = (form) => properties['sx:btNeedTaxi'];

    // рядки одного маршруту: транспорт, проживання, таксі
    const routeRows = (form, index, pinTrailing) => {
        const travelInfo = travel(form);
        const residenceInfo = residence(form);
        const method = form.properties['sx:pbtMethodTransportation'];
        const departureDate = travelInfo['sx:tfDepartureDate'];
        const arrivalDate = travelInfo['sx:tfArrivalDate'];
        return (<>
            <Row icon="pin" places={[routeFrom(form, index), routeTo(form)]} trailing={pinTrailing} />
            {method && <Row icon={transportIcon(method)}
                main={range(departureDate, arrivalDate) || fmt(departureDate) || dictLabel('sx:pbtMethodTransportation', method)}
                sub={daysText(departureDate, arrivalDate) || (departureDate ? dictLabel('sx:pbtMethodTransportation', method) : '')} />}
            {form.properties['sx:btAccommodationRequired'] && residenceInfo && <Row icon="bed"
                main={range(residenceInfo['sx:rsArrivalDate'], residenceInfo['sx:rsDepartureDate']) || i18next.t('document.tripSummaryStay')}
                sub={nightsText(residenceInfo['sx:rsArrivalDate'], residenceInfo['sx:rsDepartureDate'])} />}
            {needTaxi(form) && <Row icon="taxi" main={i18next.t('document.tripSummaryTaxi')} />}
        </>);
    }

    const backRow = backInfo?.method && <Row green icon={transportIcon(backInfo.method)}
        main={fmt(backInfo.date) || dictLabel('sx:pbtMethodTransportation', backInfo.method)}
        sub={i18next.t('document.tripSummaryReturn')} trailing="arrowUp" />;

    // загальна тривалість
    const totalDaysCount = properties['sx:btDateStart'] && properties['sx:btDateEnd']
        ? daysCount(properties['sx:btDateStart'], properties['sx:btDateEnd'])
        : null;

    // коментарі та побажання, залишені на попередніх кроках
    const comments = [];
    const addComment = (label, text) => { if (text) comments.push({ label, text }); };
    [...routes, ...(backForm ? [backForm] : [])].forEach(form => {
        addComment(i18next.t('document.tripSummaryTransportComment'), travel(form)['sx:tfPreferredDepartureTime']);
        addComment(i18next.t('document.tripSummaryTransportComment'), form.properties['sx:pbtComment']);
        addComment(i18next.t('document.tripSummaryTransportComment'), form.properties['backTravelPreferences']);
        addComment(i18next.t('document.tripSummaryStayComment'), residence(form)?.['sx:rsHotelPreferences']);
    });
    addComment(i18next.t('document.tripSummaryTransportComment'), properties['sx:btTransportationPreferences']);
    addComment(i18next.t('document.tripSummaryStayComment'), properties['sx:btAccommodationPreferences']);

    return (<div className="trip-summary">
        {autoMode && <div className="trip-summary-auto-note">
            <InfoBox text={i18next.t('document.tripSummaryAutoMode')} />
        </div>}

        {multi
            ? <>
                {routes.map((form, index) => (
                    <div className="trip-summary-block" key={index}>
                        <div className="trip-summary-header">
                            <div className="title">{routeTitle(form, index)}</div>
                            <div className="dates">{range(travel(form)['sx:tfDepartureDate'], segmentEndDate(index))}</div>
                        </div>
                        <div className="trip-summary-card">{routeRows(form, index, 'arrowDown')}</div>
                    </div>
                ))}
                {backInfo && <div className="trip-summary-block">
                    <div className="trip-summary-header green">
                        <div className="title">{i18next.t('document.tripSummaryReturn')}</div>
                        <div className="dates">{fmt(backInfo.date)}</div>
                    </div>
                    <div className="trip-summary-card">
                        <Row green icon="pin" places={[backInfo.from, backInfo.to]} trailing="arrowUp" />
                        {backRow}
                    </div>
                </div>}
            </>
            : routes.length > 0 && <div className="trip-summary-block">
                <div className="trip-summary-header">
                    <div className="title">{routeTitle(routes[0], 0)}</div>
                    <div className="dates">{range(properties['sx:btDateStart'], properties['sx:btDateEnd'])}</div>
                </div>
                <div className="trip-summary-card">
                    {routeRows(routes[0], 0, 'swap')}
                    {backRow}
                    {abroad && properties['sx:btHealthInsurance'] && <Row icon="doc" main={i18next.t('document.tripSummaryInsurance')} />}
                </div>
            </div>}

        <TripDocuments properties={properties} />

        <div className="trip-summary-info">
            <Icon name="info" />
            <div>
                {totalDaysCount !== null && <>{i18next.t('document.tripSummaryTotalDays')} {totalDaysCount} {getWordWithCorrectEnding(totalDaysCount, 'день')}<br /></>}
                {abroad || multi ? i18next.t('document.tripSummaryInfoWithTransit') : i18next.t('document.tripSummaryInfo')}
            </div>
        </div>

        {comments.length > 0 && <div className="trip-summary-comments">
            <div className="trip-summary-comments-control" onClick={() => setCommentsOpened(!commentsOpened)}>
                <span>{i18next.t('document.tripSummaryComments')}</span>
                <Icon name={commentsOpened ? 'chevUp' : 'chevDown'} size={20} />
            </div>
            {commentsOpened && <div className="trip-summary-comments-items">
                {comments.map((comment, index) => <div className="trip-summary-comment" key={index}>
                    <div className="label">{comment.label}</div>
                    <div className="text">{comment.text}</div>
                </div>)}
            </div>}
        </div>}
    </div>);
}

export default TripSummary;
